type PublisherConsents = Record<number, boolean>;

interface TCData {
  eventStatus?: 'tcloaded' | 'cmpuishown' | 'useractioncomplete' | string;
  listenerId?: number;
  publisher?: { consents?: PublisherConsents };
}

const READY_STATUSES = new Set(['tcloaded', 'useractioncomplete']);

function waitForTcfApi(timeoutMs = 3000): Promise<void> {
  if (typeof window.__tcfapi === 'function') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const id = setInterval(() => {
      if (typeof window.__tcfapi === 'function') {
        clearInterval(id);
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(id);
        reject(new Error('__tcfapi not available'));
      }
    }, 50);
  });
}

function waitForConsent(timeoutMs = 3000): Promise<TCData | undefined> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (data?: TCData) => {
      if (!settled) {
        settled = true;
        resolve(data);
      }
    };

    // Fallback timer
    const t = setTimeout(() => done(undefined), timeoutMs);

    // Prefer event subscription (fires when status changes)
    try {
      window.__tcfapi?.('addEventListener', 2, (data: TCData | undefined, success: boolean) => {
        if (!success || !data) return;
        if (READY_STATUSES.has(data.eventStatus || '')) {
          clearTimeout(t);
          // Optionally remove the listener:
          if (data.listenerId != null) {
            window.__tcfapi?.('removeEventListener', 2, () => {}, data.listenerId);
          }
          done(data);
        }
      });
    } catch {
      // ignore and fall back to a single getTCData call
    }

    // Also issue a one-shot getTCData in case we're already ready
    window.__tcfapi?.('getTCData', 2, (data: TCData | undefined, success: boolean) => {
      if (success && data && READY_STATUSES.has(data.eventStatus || '')) {
        clearTimeout(t);
        done(data);
      }
    });
  });
}

export async function hasTCFPublisherConsent(
  requiredPurposes: number[],
  timeoutMs = 6000,
): Promise<boolean> {
  try {
    await waitForTcfApi(timeoutMs);
  } catch {
    return false; // CMP never appeared within timeout
  }

  const tcData = await waitForConsent(timeoutMs);
  if (!tcData) return false;

  const consents: PublisherConsents = tcData.publisher?.consents ?? {};
  return requiredPurposes.every((p) => !!consents[p]);
}
